import { ComponentDataSourceService } from '../../component-data-source.service';
import { Observable, map, shareReplay } from 'rxjs';
import { DataSourceFunction } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { environment } from '../../../../environment/environment';
import { EventDTO, EventCoursePricingDTO } from '../../../../dto/event/event.dto';
import { EventSubEventDTO } from '../../../../dto/event/event-sub-event.dto';
import { Router } from '@angular/router';
import {
  mapApiRowsToGuidedForm,
  mergeSeriesRowsForForm,
  splitRowToFormFields,
  inferScheduleCrossesDays,
  mapLinkedGroupScheduleForForm,
  copyRowLocationToParentFormFields,
  sortSeriesDateRows,
  collapseSubEventsToStandardPlan,
  type EventScheduleMode,
  type SeriesDateRow,
} from './event-schedule-form.util';
import {
  mapAdditionalRoomsToForm,
  mapOrganizationResourceIdsToForm,
} from './event-booking-form.util';
import { resolveEventFormOrganizationId } from './event-form-org.util';
import { mapApiCateringToForm } from './event-catering-field.util';
import {
  EVENT_CATEGORY_NONE_FORM_VALUE,
  eventCategoryToFormValue,
} from './event-category-form.util';
import { AuthService } from '../../../auth/auth.service';

export const event_form_data = (service: ComponentDataSourceService): DataSourceFunction => ({
  name: 'event_form_data',
  description: 'Get event data formatted for the edit event form',
  execute: (params?: LoadDataParams): Observable<any> => {
    const router = service.injector.get(Router);
    const currentUrl = router.url || '';
    const [urlWithoutQuery, queryString = ''] = currentUrl.split('?');
    const queryParams = new URLSearchParams(queryString);
    const prefilledStartDate = queryParams.get('start_date') || undefined;
    const prefilledEndDate = queryParams.get('end_date') || undefined;
    const prefilledLocation = queryParams.get('location') || undefined;
    const prefilledRoom = queryParams.get('room') || undefined;
    const prefilledManualLocation = queryParams.get('manual_location') || undefined;
    const openCateringWishes = queryParams.get('catering') === '1';
    const prefilledOrgResourceIds = queryParams.get('organization_resource_ids') || undefined;

    if (queryString) {
      try {
        router.navigateByUrl(urlWithoutQuery, { replaceUrl: true });
      } catch {
        // Ignore navigation cleanup errors; continue parsing
      }
    }

    const pathSegments = urlWithoutQuery.split('/');
    const eventGuid = pathSegments.find(segment =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
    );
    
    if (!eventGuid) {
      console.warn('[event_form_data] No valid event GUID found in URL - this is expected for new events');
      const newEventFormData: Record<string, string | number | number[]> = {
        new_edit_copy: 'new',
        visibility: 'private',
        destination_period: 'current_period',
        event_active: 'active',
        is_shown: 'shown',
        waitlist: 'room_size',
        event_category: EVENT_CATEGORY_NONE_FORM_VALUE,
      };
      const auth = service.injector.get(AuthService);
      const orgId = resolveEventFormOrganizationId(undefined, auth);
      if (orgId != null) {
        newEventFormData['organizations_id'] = orgId;
        newEventFormData['organization_id'] = orgId;
      }
      if (prefilledStartDate) {
        newEventFormData['start_date'] = prefilledStartDate;
      }
      if (prefilledEndDate) {
        newEventFormData['end_date'] = prefilledEndDate;
      } else if (prefilledStartDate) {
        newEventFormData['end_date'] = prefilledStartDate;
      }
      if (prefilledLocation) {
        newEventFormData['location'] = prefilledLocation;
      }
      if (prefilledRoom) {
        newEventFormData['room'] = prefilledRoom;
      }
      if (prefilledManualLocation) {
        newEventFormData['manual_location'] = prefilledManualLocation;
      }
      if (prefilledOrgResourceIds) {
        const parsedIds = prefilledOrgResourceIds
          .split(',')
          .map((value) => parseInt(value.trim(), 10))
          .filter((id) => Number.isFinite(id) && id > 0);
        if (parsedIds.length > 0) {
          newEventFormData['organization_resource_ids'] = parsedIds;
        }
      }
      return new Observable(observer => {
        observer.next(newEventFormData);
        observer.complete();
      });
    }

    return service.http.get<EventDTO>(`${environment.apiUrl}/events/${eventGuid}`).pipe(
      map((eventData: EventDTO) => {
        const apiDurationType = resolveDurationTypeForForm(eventData);
        const subEventRows = mapSubEventsForForm(eventData);
        let guidedSchedule;
        let primarySessionGuid: string | null = null;
        let seriesScheduleEditable = false;

        if (eventData.linked_event_group_id) {
          const linkedSchedule = mapLinkedGroupScheduleForForm(eventData, subEventRows);
          guidedSchedule = linkedSchedule;
          primarySessionGuid = linkedSchedule.primary_session_guid;
          seriesScheduleEditable = linkedSchedule.series_schedule_editable;
        } else {
          const seriesMerged = mergeSeriesRowsForForm(
            apiDurationType,
            eventData.start_date,
            eventData.end_date,
            subEventRows
          );
          guidedSchedule = mapApiRowsToGuidedForm(
            apiDurationType,
            eventData.start_date,
            eventData.end_date,
            seriesMerged.rows
          );
        }

        const isSeriesAnchor = seriesScheduleEditable || isSeriesAnchorForForm(eventData);

        let scheduleMode = 'period';
        let seriesPlannerFields: Record<string, unknown> = {};
        if (eventData.linked_event_group_id && subEventRows.length > 1) {
          scheduleMode = 'multi_dates';
          const plan = collapseSubEventsToStandardPlan(
            'multiple_dates_locations',
            sortSeriesDateRows(subEventRows)
          );
          seriesPlannerFields = {
            series_planner_active: 'true',
            series_planning_mode: plan.planningMode,
            series_default_start_time: plan.series_default_start_time,
            series_default_end_time: plan.series_default_end_time,
            series_dates: JSON.stringify(plan.series_dates),
            series_exceptions: plan.series_exceptions,
          };
        }

        const skipAsExceptions = (eventData.multi_day_skip_dates || []).map(
          (skipDate) => ({
            exception_date: skipDate,
            exception_skip: true,
          })
        );

        const formData: any = {
          new_edit_copy: 'edit',
          event_id: eventData.event_id,
          event_guid: eventData.guid,
          event_name: getDescriptionText(eventData.description?.short_description),
          open_catering_wishes: openCateringWishes ? 'true' : 'false',
          event_type: eventData.event_type,
          schedule_mode: scheduleMode,
          event_category: eventCategoryToFormValue(
            eventData.event_extra?.category ?? null
          ),
          ...seriesPlannerFields,
          schedule_inferred_mode: guidedSchedule.schedule_inferred_mode,
          schedule_crosses_days: inferScheduleCrossesDays({
            start_date: guidedSchedule.start_date,
            end_date: guidedSchedule.end_date,
          })
            ? 'true'
            : 'false',
          series_share_participants:
            eventData.series_share_participants === true ? 'true' : 'false',
          start_date: guidedSchedule.start_date,
          end_date: guidedSchedule.end_date,
          additional_dates: guidedSchedule.additional_dates,
          visibility: eventData.visibility,
          event_duration_type: apiDurationType,
          
          linked_event_group_id: eventData.linked_event_group_id || null,
          parent_event_id: eventData.parent_event_id || null,
          is_series_anchor: isSeriesAnchor ? 'true' : 'false',
          series_schedule_editable: seriesScheduleEditable ? 'true' : 'false',
          primary_session_guid: primarySessionGuid,
          
          location: eventData.predefined_location_guid || eventData.predefined_location?.guid || 'manual',
          room: eventData.predefined_room_guid || eventData.predefined_room?.guid,
          manual_location: eventData.manual_location || '',
          manual_location_address: eventData.manual_location_address || '',
          manual_room_capacity: eventData.manual_location_room_capacity || '',
          additional_rooms: mapAdditionalRoomsToForm(eventData.additional_rooms, 'primary_extra_room'),
          organization_resource_ids: mapOrganizationResourceIdsToForm(eventData.organization_resources),
          organizations_id: eventData.organizations_id ?? eventData.organization?.id ?? null,
          organization_id: eventData.organizations_id ?? eventData.organization?.id ?? null,
          
          description: getDescriptionText(eventData.description?.long_description),
          short_description: getDescriptionText(eventData.description?.short_description),
          
          catering: mapCateringValue(eventData.catering),
          
          event_subtypes: extractSubtypes(eventData),
          event_modules: extractModules(eventData),
          destination_period: eventData.destination_period || 'current_period',
          
          event_active: eventData.is_active ? 'active' : 'inactive',
          is_shown: eventData.is_shown ? 'shown' : 'hidden',
          waitlist: mapWaitlistType(eventData.wait_list_type),
          event_price: eventData.price || 0,
          ...mapCoursePricingToFormFields(eventData.course_pricing, eventData),
          ...mapCoursePriceMetadataToFormFields(eventData),
          customer_guid: eventData.customer_guid ?? null,
          
          series_exceptions: skipAsExceptions,

          multi_day_skip_dates: [],
        };

        if (eventData.linked_event_group_id && guidedSchedule.additional_dates?.length) {
          const sortedRows = sortSeriesDateRows(subEventRows);
          if (sortedRows[0]?.sub_event_location) {
            copyRowLocationToParentFormFields(formData, sortedRows[0]);
          }
        }
        
        return formData;
      }),
      shareReplay(1)
    );
  }
});

function resolveDurationTypeForForm(eventData: EventDTO): EventScheduleMode {
  if (eventData.linked_event_group_id) {
    return 'multiple_dates_locations';
  }
  if (eventData.event_duration_type) {
    return eventData.event_duration_type;
  }
  if (eventData.parent_event_id) {
    return 'multiple_dates';
  }
  if (eventData.sub_events?.length) {
    return 'multiple_dates';
  }
  return 'single_day';
}

function isSeriesAnchorForForm(eventData: EventDTO): boolean {
  if (eventData.parent_event_id) {
    return false;
  }
  if (!eventData.linked_event_group_id) {
    return true;
  }
  // Series "anchor" is always the earliest session — series_primary is legacy.
  const siblings = eventData.sub_events || [];
  if (!siblings.length) {
    return true;
  }
  const sorted = [...siblings].sort((a, b) => {
    const aTime = new Date(a.date || '').getTime();
    const bTime = new Date(b.date || '').getTime();
    return aTime - bTime;
  });
  return sorted[0]?.guid === eventData.guid;
}

function mapSubEventsForForm(eventData: EventDTO): SeriesDateRow[] {
  const durationType = resolveDurationTypeForForm(eventData);
  return (
    eventData.sub_events?.map((subEvent: EventSubEventDTO & { sub_event_guid?: string; sub_event_date?: string }) => {
      const guid = subEvent.guid || subEvent.sub_event_guid;
      const dateTimeSource = subEvent.date
        ? subEvent.date.includes('T') || subEvent.date.includes(' ')
          ? subEvent.date
          : `${subEvent.date} ${subEvent.start_time || '12:00:00'}`
        : subEvent.sub_event_date || '';

      const subEventData: SeriesDateRow = {
        sub_event_guid: guid,
        ...splitRowToFormFields(dateTimeSource, subEvent.end_time?.substring(0, 5)),
      };

      if (durationType === 'multiple_dates' || durationType === 'multiple_dates_locations') {
        subEventData.sub_event_location =
          subEvent.predefined_location_guid || subEvent.predefined_location?.guid || 'manual';
        subEventData.sub_event_room = subEvent.predefined_room_guid || subEvent.predefined_room?.guid;
        subEventData.sub_event_manual_location = subEvent.manual_location || '';
        subEventData.sub_event_manual_location_address = subEvent.manual_location_address || '';
        subEventData.sub_event_manual_room_capacity = subEvent.manual_location_room_capacity || '';
        subEventData.sub_event_additional_rooms = mapAdditionalRoomsToForm(subEvent.additional_rooms, 'sub_extra_room');
      }
      const subCatering = (subEvent as { catering?: string }).catering;
      if (subCatering) {
        subEventData.sub_event_catering = mapApiCateringToForm(subCatering);
      }

      return subEventData;
    }) || []
  );
}

function getDescriptionText(descriptionObj?: { [key: string]: { language_code: string, translation_text: string } }): string {
  if (!descriptionObj) return '';
  
  const danishDesc = Object.values(descriptionObj).find(d => d.language_code === 'da');
  if (danishDesc) return danishDesc.translation_text;
  
  const englishDesc = Object.values(descriptionObj).find(d => d.language_code === 'en');
  if (englishDesc) return englishDesc.translation_text;
  
  const firstDesc = Object.values(descriptionObj)[0];
  return firstDesc ? firstDesc.translation_text : '';
}

function mapCateringValue(catering?: string): string {
  return mapApiCateringToForm(catering);
}

function mapWaitlistType(waitlistType?: string): string {
  switch (waitlistType) {
    case 'room_size': return 'room_size';
    case 'everyone_on_wait_list': return 'everyone_on_wait_list';
    case 'no_wait_list': return 'no_wait_list';
    default: return 'no_wait_list';
  }
}

function extractSubtypes(eventData: EventDTO): string[] {
  return eventData.items?.filter(item => item.item_type === 'subtypes')
    .map(item => item.item_id.toString()) || [];
}

function extractModules(eventData: EventDTO): string[] {
  return eventData.items?.filter(item => item.item_type === 'modules')
    .map(item => item.item_id.toString()) || [];
}

function mapCoursePricingToFormFields(coursePricing?: EventCoursePricingDTO, eventData?: EventDTO): Record<string, unknown> {
  if (!coursePricing) {
    if (eventData?.event_type === 'course' && (eventData.price ?? 0) > 0) {
      return {
        course_price_type: 'revenue_generating',
        course_revenue_model: 'fixed_course',
        course_extra_amount_enabled: 'false'
      };
    }
    return {};
  }

  const formFields: Record<string, unknown> = {
    course_price_type: coursePricing.type
  };

  if (coursePricing.type === 'revenue_generating') {
    formFields['course_revenue_model'] = coursePricing.revenue_model;
    formFields['course_extra_amount_enabled'] = coursePricing.extra_amount_enabled ? 'true' : 'false';

    if (coursePricing.extra_amount_enabled) {
      formFields['course_extra_amount'] = coursePricing.extra_amount ?? 0;
      formFields['course_extra_amount_comment'] = coursePricing.extra_amount_comment ?? '';
    }
  }

  return formFields;
}

function mapCoursePriceMetadataToFormFields(eventData: EventDTO): Record<string, unknown> {
  const fields: Record<string, unknown> = {};
  if (eventData.price_group_id != null && eventData.price_group_id > 0) {
    fields['course_price_group_id'] = eventData.price_group_id;
  }
  if (Array.isArray(eventData.course_price_template_ids) && eventData.course_price_template_ids.length > 0) {
    fields['course_price_template_ids'] = eventData.course_price_template_ids;
  }
  if (Array.isArray(eventData.price_extras) && eventData.price_extras.length > 0) {
    fields['course_price_extras'] = eventData.price_extras.map((row) => ({
      extra_option_id: row.extra_option_id,
      quantity: row.quantity ?? 0,
    }));
  }
  return fields;
}

export function buildCoursePriceMetadataFromFormData(data: any): Record<string, unknown> {
  const metadata: Record<string, unknown> = {};
  if (data.course_price_group_id != null && data.course_price_group_id !== '') {
    const groupId = Number(data.course_price_group_id);
    if (Number.isFinite(groupId) && groupId > 0) {
      metadata['course_price_group_id'] = groupId;
    }
  }
  const templateIds = Array.isArray(data.course_price_template_ids)
    ? data.course_price_template_ids
    : data.course_price_template_ids != null && data.course_price_template_ids !== ''
      ? [data.course_price_template_ids]
      : [];
  const normalizedIds = templateIds
    .map((id: unknown) => Number(id))
    .filter((id: number) => Number.isFinite(id) && id > 0);
  if (normalizedIds.length > 0) {
    metadata['course_price_template_ids'] = normalizedIds;
  }
  if (Array.isArray(data.course_price_extras)) {
    metadata['course_price_extras'] = data.course_price_extras
      .map((row: { extra_option_id?: unknown; quantity?: unknown }) => ({
        extra_option_id: Number(row?.extra_option_id),
        quantity: Math.max(0, Number(row?.quantity ?? 0)),
      }))
      .filter((row: { extra_option_id: number }) => Number.isFinite(row.extra_option_id) && row.extra_option_id > 0);
  }
  return metadata;
}

export function buildCoursePricingFromFormData(data: any): EventCoursePricingDTO | undefined {
  if (data.event_type !== 'course' || !data.course_price_type) {
    return undefined;
  }

  const pricing: EventCoursePricingDTO = {
    type: data.course_price_type
  };

  if (data.course_price_type === 'revenue_generating') {
    pricing.revenue_model = data.course_revenue_model;
    pricing.extra_amount_enabled = data.course_extra_amount_enabled === 'true' || data.course_extra_amount_enabled === true;

    if (pricing.extra_amount_enabled) {
      pricing.extra_amount = data.course_extra_amount !== undefined ? Number(data.course_extra_amount) : 0;
      pricing.extra_amount_comment = data.course_extra_amount_comment || '';
    }
  }

  return pricing;
}

export function resolveEventPriceFromFormData(data: any): number {
  if (data.event_type !== 'course') {
    return data.event_price !== undefined ? Number(data.event_price) : 0;
  }

  if (data.course_price_type === 'internal') {
    return 0;
  }

  return data.event_price !== undefined ? Number(data.event_price) : 0;
}
